import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { parseBankCsv, type BankPreset, type ParsedRow } from '../lib/csv';
import { formatDate, formatMoney } from '../lib/format';
import type { BankAccount } from '../lib/types';

export default function BankImport() {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [preset, setPreset] = useState<BankPreset>('generic');
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [csv, setCsv] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!currentOrg) return;
    supabase
      .from('dodobooks_bank_accounts')
      .select('*')
      .eq('org_id', currentOrg.id)
      .eq('is_active', true)
      .order('bank_name')
      .then(({ data }) => {
        setBankAccounts((data ?? []) as BankAccount[]);
        if ((data ?? []).length > 0 && !bankAccountId) setBankAccountId((data ?? [])[0]!.id);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg]);

  function onFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? '');
      setCsv(text);
      const result = parseBankCsv(text, preset);
      setPreset(result.preset);
      setParsed(result.rows);
      setErrors(result.errors);
    };
    reader.readAsText(file);
  }

  function onReparse(p: BankPreset) {
    setPreset(p);
    if (csv) {
      const r = parseBankCsv(csv, p);
      setParsed(r.rows);
      setErrors(r.errors);
    }
  }

  async function onImport() {
    if (!parsed || !bankAccountId) return;
    setImporting(true); setResultMsg(null);
    const rows = parsed.map((r) => ({
      bank_account_id: bankAccountId,
      txn_date: r.date,
      description: r.description,
      reference: r.reference,
      amount: r.amount,
      balance_after: r.balance_after,
      external_id: r.external_id,
      reconciled: false,
    }));
    // Chunked insert (Postgres limit on tuples).
    const chunkSize = 500;
    let inserted = 0;
    for (let i = 0; i < rows.length; i += chunkSize) {
      const slice = rows.slice(i, i + chunkSize);
      const { error } = await supabase.from('dodobooks_bank_transactions').insert(slice);
      if (error) {
        setResultMsg(`Imported ${inserted} rows, then errored: ${error.message}`);
        setImporting(false);
        return;
      }
      inserted += slice.length;
    }
    setImporting(false);
    setResultMsg(`Imported ${inserted} transactions. Head to the bank account to reconcile.`);
    setParsed(null); setCsv('');
    setTimeout(() => navigate(`/bank/${bankAccountId}`), 1500);
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-center gap-3">
        <Link to="/bank" className="btn btn-ghost"><ArrowLeft size={14} /></Link>
        <div>
          <h1 className="h-page">Import bank statement</h1>
          <p className="text-text-dim text-sm mt-1">CSV from FNB, Standard Bank, ABSA, Nedbank, Capitec, or any bank.</p>
        </div>
      </header>

      <div className="card grid grid-cols-2 gap-4">
        <div className="field">
          <label>Import into</label>
          <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
            <option value="">— pick bank account —</option>
            {bankAccounts.map((b) => <option key={b.id} value={b.id}>{b.bank_name} {b.account_number ? `· ${b.account_number}` : ''}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Bank format</label>
          <select value={preset} onChange={(e) => onReparse(e.target.value as BankPreset)}>
            <option value="generic">Auto-detect / generic</option>
            <option value="fnb">FNB</option>
            <option value="standard">Standard Bank</option>
            <option value="absa">ABSA</option>
            <option value="nedbank">Nedbank</option>
            <option value="capitec">Capitec</option>
          </select>
        </div>
      </div>

      <div className="card border-dashed border-2 border-line text-center py-10">
        <Upload size={32} className="mx-auto text-text-mute mb-3" />
        <p className="text-sm text-text-dim mb-4">Drop a CSV here or pick a file. We expect columns including Date, Description, Amount (or Debit/Credit).</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
          className="hidden"
        />
        <button onClick={() => fileInputRef.current?.click()} className="btn btn-primary">Choose CSV file</button>
      </div>

      {errors.length > 0 && (
        <div className="card border-rust/40 bg-rust/5">
          <h3 className="text-sm font-semibold text-rust mb-2">{errors.length} row(s) skipped</h3>
          <ul className="text-xs font-mono text-text-dim space-y-1 max-h-32 overflow-y-auto">
            {errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
            {errors.length > 20 && <li>… and {errors.length - 20} more</li>}
          </ul>
        </div>
      )}

      {parsed && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="h-section">Preview · {parsed.length} transactions</h2>
            <div className="flex gap-2">
              <button onClick={() => { setParsed(null); setCsv(''); }} className="btn btn-ghost"><X size={14} /> Discard</button>
              <button onClick={onImport} disabled={importing || !bankAccountId || parsed.length === 0} className="btn btn-primary disabled:opacity-50">
                <CheckCircle size={14} /> {importing ? 'Importing…' : `Import ${parsed.length}`}
              </button>
            </div>
          </div>

          <div className="card p-0 max-h-[60vh] overflow-auto">
            <table>
              <thead className="sticky top-0 bg-surface">
                <tr>
                  <th className="px-4">Date</th>
                  <th>Description</th>
                  <th>Reference</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right pr-4">Balance</th>
                </tr>
              </thead>
              <tbody>
                {parsed.slice(0, 200).map((r, i) => (
                  <tr key={i}>
                    <td className="px-4">{formatDate(r.date)}</td>
                    <td className="text-text-dim">{r.description}</td>
                    <td className="text-text-mute font-mono text-xs">{r.reference ?? ''}</td>
                    <td className={`text-right num ${r.amount < 0 ? 'text-rust' : 'text-emerald'}`}>{formatMoney(r.amount)}</td>
                    <td className="text-right num pr-4 text-text-dim">{r.balance_after != null ? formatMoney(r.balance_after) : '—'}</td>
                  </tr>
                ))}
                {parsed.length > 200 && (
                  <tr><td colSpan={5} className="text-center text-text-mute text-xs py-3">+ {parsed.length - 200} more rows (only first 200 shown)</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {resultMsg && (
        <div className="text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md">{resultMsg}</div>
      )}
    </div>
  );
}
